import React, { useRef } from "react";
import clsx from "clsx";

import InputBox from "../InputBox/InputBox";
import Button from "../../common/Button/Button";
import Icon from "../../common/Icon/Icon";
import styles from "./TextInput.module.scss";
import type { TextInputProps } from "./TextInput";
import { composeRefs } from "@radix-ui/react-compose-refs";
import { withRefProp } from "../../../utils/withRefProp";

const parseNumberish = (value: string): number | null => {
  const normalized = value.trim().replaceAll(",", "");
  if (normalized === "") return null;
  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
};

const clamp = (value: number, min?: number, max?: number): number => {
  if (min !== undefined && value < min) return min;
  if (max !== undefined && value > max) return max;
  return value;
};

const NumberInput = withRefProp((props: TextInputProps) => {
  const {
    className,
    value,
    onChange,
    onValueChange,
    placeholder,
    disabled = false,
    error = false,
    fullWidth = false,
    size = "md",
    startAdornment,
    endAdornment,
    clearable = false,
    onClear,
    clearAriaLabel = "Clear",
    ref,
    type: _type,
    min,
    max,
    step,
    defaultValue,
    ...otherProps
  } = props;

  const selfRef = useRef<HTMLInputElement | null>(null);

  const numericMin = typeof min === "number" ? min : min !== undefined ? Number(min) : undefined;
  const numericMax = typeof max === "number" ? max : max !== undefined ? Number(max) : undefined;
  const numericStep = typeof step === "number" ? step : step !== undefined ? Number(step) : 1;
  const resolvedStep = Number.isFinite(numericStep) && numericStep > 0 ? numericStep : 1;
  const [internalValue, setInternalValue] = React.useState(() => {
    if (typeof value === "string") return value;
    if (value !== undefined) return String(value);
    if (typeof defaultValue === "string") return defaultValue;
    if (defaultValue !== undefined) return String(defaultValue);
    return "";
  });
  const isControlled = value !== undefined;
  const stringValue = isControlled
    ? typeof value === "string"
      ? value
      : String(value)
    : internalValue;
  const hasValue = stringValue.length > 0;
  const showClear = clearable && !disabled && hasValue;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInternalValue(e.target.value);
    onChange?.(e);
    onValueChange?.(e.target.value);
  };

  const triggerSyntheticChange = (next: string) => {
    if (selfRef.current) {
      handleChange({ target: {...selfRef.current, value: next} } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const stepBy = (direction: 1 | -1) => {
    if (disabled) return;
    const current = parseNumberish(stringValue) ?? 0;
    const next = clamp(current + resolvedStep * direction, numericMin, numericMax);
    triggerSyntheticChange(String(next));
  };

  const clearValue = () => {
    triggerSyntheticChange("");
    onClear?.();
  };

  return (
    <InputBox
      size={size}
      forwardToInput={false}
      className={clsx("flex items-center gap-1")}
      fullWidth={fullWidth}
      error={error}
      disabled={disabled}
    >
      {startAdornment ? <span className={styles.startAdornment}>{startAdornment}</span> : null}
      <input
        ref={composeRefs(ref, selfRef)}
        className={clsx(styles.textInput, className)}
        value={stringValue}
        onChange={handleChange}
        onBlur={props.onBlur}
        onFocus={props.onFocus}
        onWheel={(event) => {
          if (document.activeElement !== event.currentTarget) return;
          if (event.deltaY < 0) stepBy(1);
          if (event.deltaY > 0) stepBy(-1);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowUp") {
            event.preventDefault();
            stepBy(1);
          }
          if (event.key === "ArrowDown") {
            event.preventDefault();
            stepBy(-1);
          }
          if (event.key === "Home" && numericMin !== undefined) {
            event.preventDefault();
            triggerSyntheticChange(String(numericMin));
          }
          if (event.key === "End" && numericMax !== undefined) {
            event.preventDefault();
            triggerSyntheticChange(String(numericMax));
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        type="text"
        inputMode="decimal"
        data-deci-textinput="true"
        data-full-width={fullWidth ? "true" : "false"}
        {...otherProps}
      />
      <span className={styles.numberStepper}>
        <Button
          icon
          type="button"
          label="Decrease value"
          tooltip={false}
          size="sm"
          className={styles.stepperButton}
          onClick={() => stepBy(-1)}
          disabled={disabled}
        >
          <Icon name="minus" />
        </Button>
        <Button
          icon
          type="button"
          label="Increase value"
          tooltip={false}
          size="sm"
          className={styles.stepperButton}
          onClick={() => stepBy(1)}
          disabled={disabled}
        >
          <Icon name="add" />
        </Button>
      </span>
      {showClear ? (
        <Button
          icon
          type="button"
          label={clearAriaLabel}
          tooltip={false}
          size="sm"
          className={styles.clearButton}
          onClick={clearValue}
        >
          <Icon name="clear" />
        </Button>
      ) : null}
      {endAdornment ? <span className={styles.endAdornment}>{endAdornment}</span> : null}
    </InputBox>
  );
});

export default NumberInput;
